import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import TimedOrCategorySearchCard from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import PageTitle from '@/components/PageTitle/PageTitle';
import { itemSortOrders } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCuratedCollectionLabel, getCuratedCollectionTotalFound } from '@/redux/modules/curatedCollection';
import { changePaginationSortOrder, getPaginationSortOrder } from '@/redux/modules/pagination';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { parseQueryParams } from '@/utils/queryParams';
import { toQueryString } from '@/utils/urls';

import CuratedCollectionPageHeaderMeta from './components/CuratedCollectionPageHeaderMeta';
import useGetSortedCollectionItemIds from './hooks/useGetSortedCollectionItemIds';

type CollectionRouteParams = {
    collectionId: string;
    collectionName: string;
};

const CURATED_COLLECTION_PAGE = 'curated-collection-page';

const CuratedCollectionPage = () => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { collectionId, collectionName } = useParams<CollectionRouteParams>();
    const queryParams = parseQueryParams(search);
    const collectionLabel = useAppSelector(getCuratedCollectionLabel);
    const count = useAppSelector(getCuratedCollectionTotalFound);
    const paginationSortOrder = useAppSelector((state) => getPaginationSortOrder(state, 'curated-collection-page'));
    const sort = queryParams.sort || paginationSortOrder || itemSortOrders.TIME_SOONEST_FIRST;
    const itemIds = useGetSortedCollectionItemIds(sort);

    const firstItemId = itemIds?.[0] || 0;

    const getSortByOption = useCallback(
        (option) => {
            const label = formatMessage(option.toLowerCase());
            return { label, value: option };
        },
        [formatMessage]
    );

    const sortByOptions = Object.keys(itemSortOrders).map(getSortByOption);

    const sortSelected = useCallback(
        ({ value }: OptionType) => {
            dispatch(changePaginationSortOrder(CURATED_COLLECTION_PAGE, value));
            navigate({
                pathname,
                search: toQueryString({
                    ...queryParams,
                    sort: value,
                }),
            });
        },
        [dispatch, pathname, navigate, queryParams]
    );

    return (
        <div className="relative mx-auto my-md flex min-h-[400px] max-w-[1240px] flex-col px-sm pb-2xl">
            <CuratedCollectionPageHeaderMeta
                collectionId={Number(collectionId)}
                collectionLabel={collectionLabel}
                collectionName={collectionName}
                itemId={firstItemId}
            />
            <div className="flex flex-row flex-wrap items-baseline [@media(max-width:416px)]:flex-col [@media(max-width:416px)]:items-center [@media(max-width:416px)]:text-center">
                <PageTitle title={collectionLabel} />
                <BodySecondary
                    className="ml-xs pr-xs"
                    color="secondary"
                >
                    {formatMessage(['search_item_count_items'], {
                        values: {
                            count: (
                                <FormattedNumber
                                    key="curated-collection-page-results-count"
                                    style="decimal"
                                    value={count}
                                />
                            ),
                        },
                    })}
                </BodySecondary>
            </div>
            <div className="mb-md smMin:mb-lg">
                <Select
                    className="min-w-[300px] max-w-[350px] smMin:float-right"
                    isInsideLabel
                    labelText={<>{formatMessage(['sort'])}:</>}
                    onChange={(option) => sortSelected(option)}
                    options={sortByOptions}
                    small
                    value={getSortByOption(sort)}
                />
            </div>
            <FluidItemGrid
                className="mb-sm"
                itemCardOption={TimedOrCategorySearchCard}
                itemIds={itemIds}
            />
        </div>
    );
};

export default CuratedCollectionPage;
